import { z } from 'zod'
import { UnitsPreference } from '../enums'

export const Email = z.string().trim().toLowerCase().pipe(z.email().max(254))
/** The shortest password Klunkers accepts. */
export const PASSWORD_MIN_LENGTH = 8
export const Password = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters.`)
  .max(200)

export const RegisterBody = z.object({
  email: Email,
  password: Password,
  units: UnitsPreference.default('metric'),
})
export type RegisterBody = z.input<typeof RegisterBody>

export const LoginBody = z.object({
  email: Email,
  password: z.string().min(1).max(200),
})
export type LoginBody = z.infer<typeof LoginBody>

/** Ask for a "reset your password" email. */
export const ForgotPasswordBody = z.object({ email: Email })
export type ForgotPasswordBody = z.infer<typeof ForgotPasswordBody>

/** Set a new password with the token from that email. Signs the rider in. */
export const ResetPasswordBody = z.object({
  token: z.string().min(1).max(200),
  password: Password,
})
export type ResetPasswordBody = z.infer<typeof ResetPasswordBody>

/** Change the password while signed in. Signs out every other device. */
export const ChangePasswordBody = z.object({
  current_password: z.string().min(1).max(200),
  new_password: Password,
})
export type ChangePasswordBody = z.infer<typeof ChangePasswordBody>

/**
 * Signing up on klunkers.bike, carrying on in the app at app.klunkers.bike:
 * the two can't share a login, so the site gets a short-lived handoff token
 * that the app swaps for a session.
 */
export const HandoffResponse = z.object({ handoff: z.string() })
export type HandoffResponse = z.infer<typeof HandoffResponse>
export const RedeemHandoffBody = z.object({ handoff: z.string().min(1).max(2000) })
export type RedeemHandoffBody = z.infer<typeof RedeemHandoffBody>

export const RefreshBody = z.object({ refresh_token: z.string().min(1) })
export type RefreshBody = z.infer<typeof RefreshBody>

/** Key/secret auth for scripts and integrations; returns the same tokens as login. */
export const KeyTokenBody = z.object({
  key_id: z.string().min(1),
  secret: z.string().min(1),
})
export type KeyTokenBody = z.infer<typeof KeyTokenBody>

export const TokenResponse = z.object({
  access_token: z.string(),
  refresh_token: z.string().optional(),
  token_type: z.literal('Bearer'),
  expires_in: z.number().int(),
})
export type TokenResponse = z.infer<typeof TokenResponse>

/** Claims carried in the access JWT. `sub` is the user id. */
export const AccessClaims = z.object({
  sub: z.uuid(),
  typ: z.literal('access'),
})
export type AccessClaims = z.infer<typeof AccessClaims>
